import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { api, API } from "../lib/api";
import { useContent } from "../lib/content";
import { toast } from "sonner";
import AboutAdmin from "../components/AboutAdmin";
import UsersAdmin from "../components/UsersAdmin";
import FaqEditor from "../components/FaqEditor";
import ContentField from "../components/ContentField";
import PlatformAdmin from "../components/PlatformAdmin";

export default function Admin() {
  const navigate = useNavigate();
  const { refresh: refreshContent } = useContent();
  const [tab, setTab] = useState("pedidos");
  const [subtab, setSubtab] = useState({ pedidos: "lista", leads: "lista", mensajes: "mensajes" });
  const [stats, setStats] = useState(null);
  // Paginated lists with search + delete
  const [orders, setOrders] = useState({ items: [], total: 0 });
  const [leads, setLeads] = useState({ items: [], total: 0 });
  const [contactMsgs, setContactMsgs] = useState({ items: [], total: 0 });
  const [testimonials, setTestimonials] = useState({ items: [], total: 0 });
  const [orderQ, setOrderQ] = useState(""); const [orderPage, setOrderPage] = useState(0);
  const [leadQ, setLeadQ] = useState("");  const [leadPage, setLeadPage]  = useState(0);
  const [msgQ, setMsgQ] = useState("");    const [msgPage, setMsgPage]   = useState(0);
  const [tstQ, setTstQ] = useState("");    const [tstPage, setTstPage]   = useState(0);
  const [tstStatus, setTstStatus] = useState("all"); // all | published | pending
  const [defaults, setDefaults] = useState({});
  const [contentValues, setContentValues] = useState({});
  const [syncing, setSyncing] = useState(false);
  // Library search + pagination
  const [libQuery, setLibQuery] = useState("");
  const [libPage, setLibPage] = useState(0);
  const [libData, setLibData] = useState({ items: [], total: 0 });

  const guard = () => {
    if (!localStorage.getItem("eterea_admin_pw")) {
      navigate("/admin/login");
      return false;
    }
    return true;
  };

  const adminAuthQS = `X-Admin-Password=${encodeURIComponent(localStorage.getItem("eterea_admin_pw") || "")}`;

  const loadAll = async () => {
    try {
      const [s, d] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/content/defaults"),
      ]);
      const { data: live } = await api.get("/content");
      setStats(s.data); setDefaults(d.data); setContentValues(live);
    } catch {
      localStorage.removeItem("eterea_admin_pw");
      navigate("/admin/login");
    }
  };

  const loadOrders = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/orders", { params: { q: orderQ, limit: 10, offset: orderPage * 10 } });
      setOrders(data);
    } catch { /* */ }
  }, [orderQ, orderPage]);
  const loadLeads = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/leads", { params: { q: leadQ, limit: 10, offset: leadPage * 10 } });
      setLeads(data);
    } catch { /* */ }
  }, [leadQ, leadPage]);
  const loadMsgs = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/contact-messages", { params: { q: msgQ, limit: 10, offset: msgPage * 10 } });
      setContactMsgs(data);
    } catch { /* */ }
  }, [msgQ, msgPage]);
  const loadTestimonials = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/testimonials", {
        params: { q: tstQ, limit: 10, offset: tstPage * 10, status: tstStatus },
      });
      setTestimonials(data);
    } catch { /* */ }
  }, [tstQ, tstPage, tstStatus]);

  const loadLibrary = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/library", {
        params: { q: libQuery, limit: 10, offset: libPage * 10 },
      });
      setLibData(data);
    } catch (e) { toast.error("Error cargando biblioteca"); }
  }, [libQuery, libPage]);

  useEffect(() => { if (guard()) loadAll(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (guard()) loadOrders(); }, [orderQ, orderPage, loadOrders]);
  useEffect(() => { if (guard()) loadLeads(); }, [leadQ, leadPage, loadLeads]);
  useEffect(() => { if (guard()) loadMsgs(); }, [msgQ, msgPage, loadMsgs]);
  useEffect(() => { if (guard()) loadTestimonials(); }, [tstQ, tstPage, tstStatus, loadTestimonials]);
  useEffect(() => { if (guard()) loadLibrary(); }, [libQuery, libPage, loadLibrary]);

  const refreshLists = () => { loadAll(); loadOrders(); loadLeads(); loadMsgs(); loadTestimonials(); };

  const logout = () => { localStorage.removeItem("eterea_admin_pw"); navigate("/admin/login"); };

  const runSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/notion/sync");
      toast.success(`Sincronización completa: ${data.imported} entradas`);
      loadLibrary(); loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error sincronizando"); }
    finally { setSyncing(false); }
  };
  const saveContent = async (key, value) => {
    try { await api.put("/admin/content", { key, value });
      setContentValues({ ...contentValues, [key]: value }); refreshContent(); toast.success("Guardado ✦");
    } catch { toast.error("No se pudo guardar"); }
  };
  const resetContent = async (key) => {
    try { await api.delete(`/admin/content/${key}`);
      const nv = { ...contentValues }; nv[key] = defaults[key]; setContentValues(nv); refreshContent();
      toast.success("Restaurado");
    } catch { toast.error("Error"); }
  };
  const uploadLogo = async (file) => {
    const reader = new FileReader();
    reader.onload = async () => {
      try { await api.put("/admin/logo", { data_url: reader.result }); refreshContent(); toast.success("Logo actualizado ✦"); }
      catch (e) { toast.error(e?.response?.data?.detail || "Falló"); }
    };
    reader.readAsDataURL(file);
  };

  const generateReport = async (orderId) => {
    try {
      await api.post(`/admin/orders/${orderId}/generate-report`);
      toast.success("Reporte generado ✦");
      refreshLists();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falló generación"); }
  };
  const sendReport = async (orderId, message) => {
    try {
      const { data } = await api.post(`/admin/orders/${orderId}/send-report`, { message });
      toast.success(`Envío: ${data.status}`);
      refreshLists();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falló envío"); }
  };
  const updateOrder = async (id, payload) => {
    try { await api.patch(`/admin/orders/${id}`, payload); toast.success("Actualizado"); refreshLists(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const deleteOrder = async (id) => {
    if (!window.confirm("¿Borrar este pedido? No se puede deshacer.")) return;
    try { await api.delete(`/admin/orders/${id}`); toast.success("Pedido borrado"); refreshLists(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const deleteLead = async (id) => {
    if (!window.confirm("¿Borrar este lead?")) return;
    try { await api.delete(`/admin/leads/${id}`); toast.success("Lead borrado"); refreshLists(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const deleteMsg = async (id) => {
    if (!window.confirm("¿Borrar este mensaje?")) return;
    try { await api.delete(`/admin/contact-messages/${id}`); toast.success("Mensaje borrado"); refreshLists(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const toggleTestimonialPublished = async (id, next) => {
    try {
      await api.patch(`/admin/testimonials/${id}`, { published: next });
      toast.success(next ? "Publicado en Testimonios ✦" : "Despublicado");
      loadTestimonials();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const toggleTestimonialFeatured = async (id, next) => {
    try {
      await api.patch(`/admin/testimonials/${id}`, { featured: next });
      toast.success(next ? "Destacado en la página principal ✦" : "Ya no está destacado");
      loadTestimonials();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const deleteTestimonial = async (id) => {
    if (!window.confirm("¿Borrar este testimonio? No se puede deshacer.")) return;
    try { await api.delete(`/admin/testimonials/${id}`); toast.success("Testimonio borrado"); loadTestimonials(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const downloadUrl = (filename) => `${API}/admin/reports/${encodeURIComponent(filename)}?${adminAuthQS}`;

  return (
    <div data-testid="admin-page" className="min-h-screen">
      <Navbar />
      <section className="pt-32 pb-20 max-w-7xl mx-auto px-6">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
          <div>
            <p className="overline mb-2">El estudio</p>
            <h1 className="font-display text-5xl text-white font-light tracking-tight">Bienvenida, astróloga.</h1>
          </div>
          <button data-testid="admin-logout" onClick={logout} className="btn-ghost">Salir</button>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            <StatCard label="Pedidos pagados" value={stats.paid_orders} />
            <StatCard label="Pendientes entregar" value={stats.pending_delivery} highlight />
            <StatCard label="Leads" value={stats.leads} />
            <StatCard label="Ingresos" value={`$${(stats.revenue_usd || 0).toFixed(2)}`} />
          </div>
        )}

        <div className="flex gap-1 border-b border-white/10 mb-8 overflow-x-auto">
          {[
            ["pedidos", "Pedidos"],
            ["leads", "Leads"],
            ["biblioteca", "Biblioteca"],
            ["notion", "Notion"],
            ["contenido", "Contenido"],
            ["logo", "Logo"],
            ["mensajes", "Mensajes"],
            ["sobremi", "Sobre mí"],
            ["usuarios", "Usuarios"],
            ["plataforma", "Plataforma de usuarios"],
          ].map(([k, l]) => (
            <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
              className={`px-4 py-3 text-sm tracking-wide whitespace-nowrap ${
                tab === k ? "text-[#C9A25E] border-b border-[#C9A25E]" : "text-zinc-500 hover:text-white"
              }`}>{l}</button>
          ))}
        </div>

        {tab === "pedidos" && (
          <>
            <SubTabBar
              testId="pedidos"
              value={subtab.pedidos}
              onChange={(v) => setSubtab({ ...subtab, pedidos: v })}
              options={[["lista", "Lista de pedidos"], ["birthdays", "🎂 Próximos cumpleaños"]]}
            />
            {subtab.pedidos === "lista" ? (
              <PaginatedList
                testId="pedidos"
                query={orderQ} setQuery={(q) => { setOrderQ(q); setOrderPage(0); }}
                page={orderPage} setPage={setOrderPage}
                total={orders.total}
                placeholder='Buscar — nombre, email, "Tríada", "2026-05", lugar…'
                empty="Aún no hay pedidos."
              >
                {orders.items.map((o) => (
                  <OrderRow key={o.id} order={o} onUpdate={updateOrder}
                    onGenerate={generateReport} onSend={sendReport}
                    onDelete={deleteOrder} downloadUrl={downloadUrl} />
                ))}
              </PaginatedList>
            ) : (
              <BirthdaysCalendar source="orders" />
            )}
          </>
        )}

        {tab === "leads" && (
          <div>
            <div className="mb-6 p-4 border border-[#C9A25E]/20 bg-[#C9A25E]/5 text-zinc-300 text-sm">
              <span className="overline mr-2">¿Qué es esto?</span>
              Leads son personas que usaron la calculadora <strong>Carta Gratis</strong> y compartieron nombre + correo
              para verla. <strong>No</strong> son clientes pagantes — esos aparecen en la pestaña Pedidos. Útil para campañas / newsletter.
            </div>
            <SubTabBar
              testId="leads"
              value={subtab.leads}
              onChange={(v) => setSubtab({ ...subtab, leads: v })}
              options={[["lista", "Lista de leads"], ["birthdays", "🎂 Próximos cumpleaños"]]}
            />
            {subtab.leads === "lista" ? (
              <PaginatedList
                testId="leads"
                query={leadQ} setQuery={(q) => { setLeadQ(q); setLeadPage(0); }}
                page={leadPage} setPage={setLeadPage}
                total={leads.total}
                placeholder='Buscar nombre, email, fecha de nacimiento o lugar…'
                empty="No hay leads todavía."
              >
                {leads.items.map((l) => (
                  <div key={l.id} data-testid={`lead-${l.id}`} className="card !p-5 flex flex-wrap justify-between items-center gap-3">
                    <div>
                      <p className="font-display text-xl text-white">{l.name}</p>
                      <p className="text-xs text-zinc-500">{l.email}</p>
                      <p className="text-xs text-zinc-600 mt-1">Nacida {l.birth_date} · {l.birth_place}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <p className="text-xs text-zinc-500">{new Date(l.created_at).toLocaleDateString("es")}</p>
                      <button onClick={() => deleteLead(l.id)}
                        data-testid={`lead-delete-${l.id}`}
                        className="text-xs text-zinc-500 hover:text-red-400">Borrar</button>
                    </div>
                  </div>
                ))}
              </PaginatedList>
            ) : (
              <BirthdaysCalendar source="leads" />
            )}
          </div>
        )}

        {tab === "biblioteca" && (
          <LibraryTab
            libData={libData}
            query={libQuery}
            setQuery={(q) => { setLibQuery(q); setLibPage(0); }}
            page={libPage}
            setPage={setLibPage}
          />
        )}

        {tab === "notion" && (
          <div className="max-w-xl space-y-6">
            <div>
              <p className="overline mb-2">Sincronización con Notion</p>
              <p className="text-zinc-400 text-sm mb-4">
                Tu biblioteca se resincroniza automáticamente <strong>cada hora</strong>. Pulsa para hacerlo ahora.
              </p>
              <button onClick={runSync} disabled={syncing} data-testid="notion-sync" className="btn-lilac">
                {syncing ? "Sincronizando…" : "Sincronizar ahora ✦"}
              </button>
            </div>
            <div className="card">
              <p className="overline mb-3">Estado actual</p>
              <p className="text-zinc-400 text-sm">{libData.total} entradas en biblioteca local.</p>
              {contentValues.notion_last_sync && (
                <p className="text-xs text-zinc-500 mt-2">Última sync: {new Date(contentValues.notion_last_sync).toLocaleString("es")}</p>
              )}
            </div>
          </div>
        )}

        {tab === "contenido" && (
          <ContentEditor defaults={defaults} values={contentValues}
            onSave={saveContent} onReset={resetContent} refreshAll={loadAll} />
        )}

        {tab === "plataforma" && (
          <PlatformAdmin defaults={defaults} values={contentValues} onSave={saveContent} onReset={resetContent} />
        )}

        {tab === "logo" && (
          <div className="max-w-xl space-y-6">
            <p className="overline mb-2">Logo de Etérea Enlace</p>
            <p className="text-zinc-400 text-sm mb-4">PNG transparente recomendado (máx 1.5 MB).</p>
            <input type="file" accept="image/*" data-testid="logo-upload"
              onChange={(e) => e.target.files?.[0] && uploadLogo(e.target.files[0])}
              className="text-zinc-400 text-sm file:btn-lilac file:border-0 file:mr-3 file:cursor-pointer" />
          </div>
        )}

        {tab === "mensajes" && (
          <>
            <SubTabBar
              testId="mensajes"
              value={subtab.mensajes}
              onChange={(v) => setSubtab({ ...subtab, mensajes: v })}
              options={[["mensajes", "Mensajes de contacto"], ["testimonios", "✦ Testimonios"]]}
            />
            {subtab.mensajes === "mensajes" ? (
              <PaginatedList
                testId="mensajes"
                query={msgQ} setQuery={(q) => { setMsgQ(q); setMsgPage(0); }}
                page={msgPage} setPage={setMsgPage}
                total={contactMsgs.total}
                placeholder='Buscar por nombre, email o contenido…'
                empty="Sin mensajes."
              >
                {contactMsgs.items.map((m) => (
                  <div key={m.id} className="card !p-5">
                    <div className="flex justify-between items-start gap-3">
                      <div>
                        <p className="font-display text-lg text-white">{m.name}</p>
                        <p className="text-xs text-zinc-500">{m.email} · {new Date(m.created_at).toLocaleString("es")}</p>
                      </div>
                      <button onClick={() => deleteMsg(m.id)}
                        data-testid={`msg-delete-${m.id}`}
                        className="text-xs text-zinc-500 hover:text-red-400">Borrar</button>
                    </div>
                    <p className="text-zinc-300 mt-3 whitespace-pre-wrap">{m.message}</p>
                  </div>
                ))}
              </PaginatedList>
            ) : (
              <TestimonialsAdmin
                data={testimonials}
                query={tstQ} setQuery={(q) => { setTstQ(q); setTstPage(0); }}
                page={tstPage} setPage={setTstPage}
                status={tstStatus} setStatus={(s) => { setTstStatus(s); setTstPage(0); }}
                onTogglePublish={toggleTestimonialPublished}
                onToggleFeatured={toggleTestimonialFeatured}
                onDelete={deleteTestimonial}
              />
            )}
          </>
        )}

        {tab === "sobremi" && <AboutAdmin />}
        {tab === "usuarios" && <UsersAdmin />}
      </section>
      <Footer />
    </div>
  );
}

function TestimonialsAdmin({ data, query, setQuery, page, setPage, status, setStatus, onTogglePublish, onToggleFeatured, onDelete }) {
  const totalPages = Math.max(1, Math.ceil(data.total / 10));
  return (
    <div data-testid="testimonials-admin">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex flex-wrap gap-2 items-center">
          <input
            data-testid="testimonials-search"
            className="input-field min-w-[260px]"
            placeholder="Buscar por nombre o contenido…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <select
            data-testid="testimonials-status"
            className="input-field"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="all">Todos</option>
            <option value="published">Publicados</option>
            <option value="pending">Sin publicar</option>
          </select>
        </div>
        <p className="text-sm text-zinc-500">{data.total} en total · página {page + 1}/{totalPages}</p>
      </div>

      <div className="mb-6 p-4 border border-[#C9A25E]/20 bg-[#C9A25E]/5 text-zinc-300 text-sm">
        <span className="overline mr-2">¿Cómo funciona?</span>
        Los testimonios llegan desde la página <strong>Testimonios</strong>. Los <strong>publicados</strong> aparecen
        en esa página; en la página principal sólo salen los <strong>destacados</strong>.
      </div>

      <div className="space-y-3 mb-6">
        {data.items.length === 0 && <p className="text-zinc-500 italic">Sin testimonios todavía.</p>}
        {data.items.map((tt) => (
          <div key={tt.id} data-testid={`testimonial-row-${tt.id}`} className={`card !p-5 ${tt.published ? "border-[#C9A25E]/40" : ""}`}>
            <div className="flex flex-wrap justify-between items-start gap-3 mb-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-display text-lg text-white">{tt.name || "Anónimo"}</p>
                  {tt.rating ? (
                    <span className="text-[#C9A25E] text-xs tracking-widest">{"✦".repeat(tt.rating)}</span>
                  ) : null}
                  {tt.published ? (
                    <span className="overline !text-emerald-400">Publicado</span>
                  ) : (
                    <span className="overline !text-zinc-500">Borrador</span>
                  )}
                  {tt.featured && <span data-testid={`testimonial-featured-badge-${tt.id}`} className="overline !text-[#C9A25E]">★ Destacado</span>}
                </div>
                <p className="text-xs text-zinc-500 mt-1">{new Date(tt.created_at).toLocaleString("es")}</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => onTogglePublish(tt.id, !tt.published)}
                  data-testid={`testimonial-toggle-${tt.id}`}
                  className={tt.published ? "btn-ghost !py-2 !px-3 text-xs" : "btn-lilac !py-2 !px-3 text-xs"}
                >
                  {tt.published ? "↓ Despublicar" : "↑ Publicar"}
                </button>
                <button
                  onClick={() => onToggleFeatured(tt.id, !tt.featured)}
                  data-testid={`testimonial-feature-${tt.id}`}
                  className="btn-ghost !py-2 !px-3 text-xs"
                >
                  {tt.featured ? "☆ Quitar destacado" : "★ Destacar en principal"}
                </button>
                <button
                  onClick={() => onDelete(tt.id)}
                  data-testid={`testimonial-delete-${tt.id}`}
                  className="text-xs text-zinc-500 hover:text-red-400"
                >Borrar</button>
              </div>
            </div>
            <p className="text-zinc-200 whitespace-pre-wrap text-sm leading-relaxed">{tt.message}</p>
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <button data-testid="testimonials-prev" onClick={() => setPage(Math.max(0, page - 1))}
            disabled={page === 0} className="btn-ghost text-sm">← Anterior</button>
          <button data-testid="testimonials-next" onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
            disabled={page >= totalPages - 1} className="btn-ghost text-sm">Siguiente →</button>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, highlight }) {
  return (
    <div className={`card !p-6 ${highlight ? "border-[#C9A25E]/30" : ""}`}>
      <p className="overline mb-2">{label}</p>
      <p className={`font-display text-4xl ${highlight ? "text-[#C9A25E]" : "text-white"}`}>{value}</p>
    </div>
  );
}

function SubTabBar({ testId, value, onChange, options }) {
  return (
    <div className="flex gap-1 border-b border-white/5 mb-6 -mt-2">
      {options.map(([k, l]) => (
        <button key={k}
          data-testid={`subtab-${testId}-${k}`}
          onClick={() => onChange(k)}
          className={`px-4 py-2 text-xs tracking-wide whitespace-nowrap ${
            value === k ? "text-[#C9A25E] border-b border-[#C9A25E]" : "text-zinc-500 hover:text-white"
          }`}>{l}</button>
      ))}
    </div>
  );
}

function PaginatedList({ testId, query, setQuery, page, setPage, total, placeholder, empty, children }) {
  const totalPages = Math.max(1, Math.ceil(total / 10));
  const hasItems = Array.isArray(children) ? children.length > 0 : Boolean(children);
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <input
          data-testid={`${testId}-search`}
          className="input-field max-w-md"
          placeholder={placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <p className="text-sm text-zinc-500">{total} en total · página {page + 1}/{totalPages}</p>
      </div>
      <div className="space-y-3 mb-6">
        {hasItems ? children : <p className="text-zinc-500 italic">{empty}</p>}
      </div>
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <button data-testid={`${testId}-prev`} onClick={() => setPage(Math.max(0, page - 1))}
            disabled={page === 0} className="btn-ghost text-sm">← Anterior</button>
          <button data-testid={`${testId}-next`} onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
            disabled={page >= totalPages - 1} className="btn-ghost text-sm">Siguiente →</button>
        </div>
      )}
    </div>
  );
}

function LibraryTab({ libData, query, setQuery, page, setPage }) {
  const totalPages = Math.max(1, Math.ceil(libData.total / 10));
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <input
          data-testid="library-search"
          className="input-field max-w-md"
          placeholder='Buscar — ej. "Sol en Aries", "Luna conjunción Sol"'
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <p className="text-sm text-zinc-500">{libData.total} entradas totales · página {page + 1}/{totalPages}</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        {libData.items.map((e) => (
          <div key={e.id} className="card !p-5">
            <span className="overline">{kindLabel(e)}</span>
            <h3 className="font-display text-xl text-white mt-2 mb-2">{e.title}</h3>
            <p className="text-zinc-400 text-sm line-clamp-3 whitespace-pre-wrap">{e.body}</p>
          </div>
        ))}
        {libData.items.length === 0 && (
          <p className="text-zinc-500 italic col-span-2">Sin resultados.</p>
        )}
      </div>
      <div className="flex justify-center gap-2">
        <button data-testid="lib-prev" onClick={() => setPage(Math.max(0, page - 1))}
          disabled={page === 0} className="btn-ghost text-sm">← Anterior</button>
        <button data-testid="lib-next" onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
          disabled={page >= totalPages - 1} className="btn-ghost text-sm">Siguiente →</button>
      </div>
    </div>
  );
}

function kindLabel(e) {
  switch (e.kind) {
    case "planet_in_sign": return `${e.planet || "?"} en ${e.sign || "?"}`;
    case "planet_in_house": return `${e.planet || "?"} en Casa ${e.house ?? "?"}`;
    case "aspect": return `${e.planet || "?"} ${e.aspect_type || ""} ${e.second_planet || ""}`;
    case "ascendant_in_sign": return `Ascendente en ${e.sign || "?"}`;
    default: return e.kind;
  }
}

function OrderRow({ order, onUpdate, onGenerate, onSend, onDelete, downloadUrl }) {
  const [expanded, setExpanded] = useState(false);
  const [message, setMessage] = useState(order.delivery_message || "");
  const READING_LABELS = {
    "Tríada": "El Faro Interno",
    "Personal": "Las Herramientas del Navegante",
    "Completo": "La Travesía Completa",
    "Revolución Solar": "Revolución Solar",
  };
  const planLabel = order.plan === "session" ? "Reporte + Sesión 1:1" : "Solo Reporte";
  const readingLabel = READING_LABELS[order.reading_type] || order.reading_type;
  const statusColor = {
    pending: "text-zinc-500", in_progress: "text-[#C9A25E]", delivered: "text-emerald-400",
  }[order.status] || "text-zinc-500";

  return (
    <div data-testid={`order-${order.id}`} className="card !p-5">
      <div className="flex flex-wrap items-center justify-between gap-4 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-display text-xl text-white">{order.name}</p>
            <span className={`overline ${statusColor}`}>
              {order.paid ? (order.status === "delivered" ? "Entregado" : order.status === "in_progress" ? "En curso" : "Pendiente") : "Sin pagar"}
            </span>
            {order.payment_method === "whatsapp" && (
              <span className="overline !text-emerald-400">💬 WhatsApp</span>
            )}
            {order.report_pdf_filename && (
              <span className="overline !text-emerald-400">PDF listo</span>
            )}
          </div>
          <p className="text-xs text-zinc-500">{order.email} · {planLabel} · <span className="text-[#C9A25E]">{readingLabel}</span></p>
          <p className="text-xs text-zinc-600 mt-1">Nacida {order.birth_date} {order.birth_time} · {order.birth_place}</p>
        </div>
        <div className="text-right">
          <p className="text-white font-display text-2xl">${order.amount}</p>
          <p className="text-xs text-zinc-500">{new Date(order.created_at).toLocaleDateString("es")}</p>
        </div>
      </div>

      {expanded && (
        <div className="mt-6 pt-6 border-t border-white/10 space-y-4">
          {order.notes && (
            <div>
              <p className="overline mb-1">Notas del cliente</p>
              <p className="text-zinc-300 text-sm whitespace-pre-wrap">{order.notes}</p>
            </div>
          )}

          {/* Manual payment confirmation (WhatsApp flow) */}
          {!order.paid && order.payment_method === "whatsapp" && (
            <div className="p-4 border border-emerald-400/25 bg-emerald-400/5 space-y-2">
              <p className="text-sm text-zinc-300">
                Pedido por <strong className="text-white">WhatsApp</strong> — pago manual (Wise / Prex).
                Cuando te llegue la transferencia, confírmalo acá y se genera el reporte + email automáticamente.
              </p>
              <button onClick={() => onUpdate(order.id, { status: order.status, paid: true })}
                data-testid={`mark-paid-${order.id}`} className="btn-lilac text-sm">
                ✓ Marcar como pagado
              </button>
            </div>
          )}

          {order.paid && (
            <>
              {/* Report generation */}
              <div className="space-y-2">
                <p className="overline">Reporte automatizado</p>
                {!order.report_pdf_filename ? (
                  order.reading_type === "Revolución Solar" ? (
                    <p className="text-zinc-500 text-sm italic">Revolución Solar se prepara manualmente.</p>
                  ) : (
                    <button onClick={() => onGenerate(order.id)} className="btn-ghost text-sm"
                      data-testid={`gen-report-${order.id}`}>Generar reporte (DOCX + PDF)</button>
                  )
                ) : (
                  <div className="flex flex-wrap gap-2">
                    <a href={downloadUrl(order.report_docx_filename)} className="btn-ghost text-sm">↓ DOCX (editable)</a>
                    <a href={downloadUrl(order.report_pdf_filename)} className="btn-ghost text-sm">↓ PDF</a>
                    <button onClick={() => onGenerate(order.id)} className="btn-ghost text-sm">↻ Regenerar</button>
                  </div>
                )}
              </div>

              {/* Send to client */}
              {order.status !== "delivered" && order.report_pdf_filename && (
                <>
                  <div>
                    <label className="field-label">Mensaje al cliente (acompaña el PDF)</label>
                    <textarea rows={3} className="input-field" value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Hola, te envío tu reporte personalizado…" />
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {order.status === "pending" && (
                      <button onClick={() => onUpdate(order.id, { status: "in_progress" })}
                        className="btn-ghost text-sm">Marcar en curso</button>
                    )}
                    <button onClick={() => onSend(order.id, message)}
                      data-testid={`send-${order.id}`} className="btn-lilac text-sm">
                      Enviar PDF al cliente ✦
                    </button>
                  </div>
                </>
              )}

              {order.status === "delivered" && (
                <div className="text-sm text-emerald-400/80">
                  Entregado el {new Date(order.delivered_at).toLocaleString("es")}
                </div>
              )}
            </>
          )}

          <div className="pt-4 border-t border-white/5 flex justify-end">
            <button onClick={(e) => { e.stopPropagation(); onDelete(order.id); }}
              data-testid={`order-delete-${order.id}`}
              className="text-xs text-zinc-500 hover:text-red-400">Borrar pedido</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ContentEditor({ defaults, values, onSave, onReset, refreshAll }) {
  // Friendly Spanish labels per key — anything not listed will appear under "Otros"
  const KEY_LABELS = {
    // Marca / Navbar
    brand_name: "Nombre de la marca",
    brand_tagline: "Tagline corto",
    nav_carta: "Menú · Carta Gratis",
    nav_solicitar: "Menú · Solicitar mi lectura",
    nav_contacto: "Menú · Contacto",
    nav_cta: "Botón CTA del menú",
    // Hero
    hero_overline: "Hero · Pre-título (overline)",
    hero_title: "Hero · Título principal",
    hero_title_emphasis: "Hero · Palabras a resaltar (en lila)",
    hero_subtitle: "Hero · Subtítulo",
    hero_cta_primary: "Hero · Botón primario",
    hero_cta_secondary: "Hero · Botón secundario",
    hero_scroll_hint: "Hero · Texto inferior",
    // Features
    features_overline: "Features · Overline",
    features_title: "Features · Título",
    features_title_emphasis: "Features · Énfasis (lila)",
    features_1_step: "Paso 01 · Número",
    features_1_title: "Paso 01 · Título",
    features_1_body: "Paso 01 · Descripción",
    features_2_step: "Paso 02 · Número",
    features_2_title: "Paso 02 · Título",
    features_2_body: "Paso 02 · Descripción",
    features_3_step: "Paso 03 · Número",
    features_3_title: "Paso 03 · Título",
    features_3_body: "Paso 03 · Descripción",
    features_4_step: "Paso 04 · Número",
    features_4_title: "Paso 04 · Título",
    features_4_body: "Paso 04 · Descripción",
    features_5_step: "Paso 05 · Número",
    features_5_title: "Paso 05 · Título",
    features_5_body: "Paso 05 · Descripción",
    // Planes (en homepage)
    plans_overline: "Planes · Overline",
    plans_title: "Planes · Título",
    plan_free_name: "Plan Gratis · Nombre",
    plan_free_price: "Plan Gratis · Precio mostrado",
    plan_free_subtitle: "Plan Gratis · Subtítulo",
    plan_free_description: "Plan Gratis · Descripción",
    plan_free_includes: "Plan Gratis · Incluye (una línea por ítem)",
    plan_free_cta: "Plan Gratis · Botón",
    plan_premium_name: "Plan Reporte · Nombre",
    plan_premium_price: "Plan Reporte · (informativo — el precio real se setea en Precios)",
    plan_premium_subtitle: "Plan Reporte · Subtítulo",
    plan_premium_description: "Plan Reporte · Descripción",
    plan_premium_includes: "Plan Reporte · Incluye",
    plan_premium_cta: "Plan Reporte · Botón",
    plan_session_name: "Plan Sesión · Nombre",
    plan_session_price: "Plan Sesión · (informativo — el precio real se setea en Precios)",
    plan_session_subtitle: "Plan Sesión · Subtítulo",
    plan_session_description: "Plan Sesión · Descripción",
    plan_session_includes: "Plan Sesión · Incluye",
    plan_session_cta: "Plan Sesión · Botón",
    // Tipos de lectura
    reading_type_label: "Selector · Etiqueta",
    reading_type_triada: "Lectura 1 · Nombre",
    reading_type_triada_subtitle: "Lectura 1 · Subtítulo",
    reading_type_personal: "Lectura 2 · Nombre",
    reading_type_personal_subtitle: "Lectura 2 · Subtítulo",
    reading_type_completo: "Lectura 3 · Nombre",
    reading_type_completo_subtitle: "Lectura 3 · Subtítulo",
    reading_type_revolucion: "Lectura 4 · Nombre",
    reading_type_revolucion_subtitle: "Lectura 4 · Subtítulo",
    // Carta gratis
    carta_overline: "Carta · Overline",
    carta_title: "Carta · Título",
    carta_subtitle: "Carta · Subtítulo",
    carta_gate_title: "Carta · Título del formulario",
    carta_gate_body: "Carta · Cuerpo del formulario",
    carta_label_name: "Carta · Label nombre",
    carta_label_email: "Carta · Label email",
    carta_label_date: "Carta · Label fecha",
    carta_label_time: "Carta · Label hora",
    carta_label_place: "Carta · Label lugar",
    carta_submit: "Carta · Botón enviar",
    carta_loading: "Carta · Texto de carga",
    carta_result_title: "Carta · Título resultado",
    carta_unlock_title: "Carta · CTA tras resultado",
    carta_unlock_body: "Carta · Texto CTA",
    carta_unlock_cta: "Carta · Botón CTA",
    // Solicitar
    solicitar_overline: "Solicitar · Overline",
    solicitar_title: "Solicitar · Título",
    solicitar_subtitle: "Solicitar · Subtítulo",
    solicitar_plan_label: "Solicitar · Etiqueta planes",
    solicitar_step1_label: "Paso 1 · Encabezado (Elige tu lectura)",
    solicitar_step2_label: "Paso 2 · Encabezado (Elige tu paquete)",
    solicitar_step3_label: "Paso 3 · Encabezado (Tus datos)",
    solicitar_plan_premium_label: "Paquete · Etiqueta 'Solo Reporte'",
    solicitar_plan_session_label: "Paquete · Etiqueta 'Reporte + Sesión 1:1'",
    solicitar_plan_session_2_label: "Paquete · Etiqueta 'Reporte + 2 Sesiones' (Travesía Completa)",
    solicitar_field_name: "Campo · Label nombre",
    solicitar_field_email: "Campo · Label email",
    solicitar_field_date: "Campo · Label fecha",
    solicitar_field_time: "Campo · Label hora",
    solicitar_field_place: "Campo · Label lugar",
    solicitar_field_place_placeholder: "Campo · Placeholder lugar",
    solicitar_field_notes: "Campo · Label notas",
    solicitar_field_notes_placeholder: "Campo · Placeholder notas",
    solicitar_total_label: "Total · Etiqueta",
    solicitar_total_currency_note: "Total · Nota sobre moneda",
    solicitar_total_empty: "Total · Mensaje cuando no hay selección",
    solicitar_loading_label: "Botón · Texto mientras carga",
    solicitar_submit: "Solicitar · Botón pagar",
    solicitar_payment_note: "Solicitar · Nota de pago (modo Stripe)",
    solicitar_whatsapp_note: "Solicitar · Nota bajo botón (modo WhatsApp)",
    whatsapp_number: "WhatsApp · Número con código de país (ej. 59899123456) — si está vacío se usa Stripe",
    whatsapp_message_template: "WhatsApp · Plantilla de mensaje — variables: {nombre} {servicio} {plan} {precio}",
    admin_notification_email: "Tu email — recibe un aviso con cada nueva solicitud de lectura",
    solicitar_session_note: "Solicitar · Nota de sesión",
    solicitar_session_extra_note: "Solicitar · Nota extra (2 sesiones)",
    // Contacto
    contacto_overline: "Contacto · Overline",
    contacto_title: "Contacto · Título",
    contacto_subtitle: "Contacto · Subtítulo",
    contacto_label_name: "Contacto · Label nombre",
    contacto_label_email: "Contacto · Label email",
    contacto_label_message: "Contacto · Label mensaje",
    contacto_submit: "Contacto · Botón enviar",
    contacto_success: "Contacto · Mensaje de éxito",
    // Exito / Lectura
    exito_overline: "Éxito · Overline",
    exito_processing: "Éxito · Procesando",
    exito_paid: "Éxito · Pago confirmado",
    exito_redirect: "Éxito · Redirección",
    lectura_overline: "Lectura · Overline",
    // Footer
    footer_tagline: "Footer · Tagline",
    footer_privacy: "Footer · Privacidad",
    footer_terms: "Footer · Términos",
    footer_rights: "Footer · Derechos",
    // Emails
    birthday_subject: "Email cumpleaños · Asunto",
    birthday_html: "Email cumpleaños · HTML",
    order_received_subject: "Email pedido · Asunto",
    order_received_html: "Email pedido · HTML",
    // Testimonios — sección homepage
    testimonials_overline: "Testimonios · Overline",
    testimonials_title: "Testimonios · Título",
    testimonials_subtitle: "Testimonios · Subtítulo",
    testimonials_empty: "Testimonios · Mensaje cuando no hay publicados (no se muestra)",
    // Testimonios — formulario en página de Contacto
    testimonial_form_overline: "Form testimonio · Overline",
    testimonial_form_title: "Form testimonio · Título",
    testimonial_form_subtitle: "Form testimonio · Subtítulo",
    testimonial_form_label_name: "Form testimonio · Label nombre",
    testimonial_form_name_placeholder: "Form testimonio · Placeholder nombre",
    testimonial_form_label_message: "Form testimonio · Label mensaje",
    testimonial_form_message_placeholder: "Form testimonio · Placeholder mensaje",
    testimonial_form_label_rating: "Form testimonio · Label estrellas",
    testimonial_form_submit: "Form testimonio · Botón enviar",
    testimonial_form_success: "Form testimonio · Mensaje de éxito",
    testimonial_form_note: "Form testimonio · Nota inferior",
  };

  // Definition of UI sections (each holds an ordered list of keys)
  const SECTIONS = [
    {
      id: "marca", label: "🪐 Marca y menú",
      keys: ["brand_name", "brand_tagline", "nav_carta", "nav_solicitar", "nav_contacto", "nav_cta"],
    },
    {
      id: "homepage", label: "🏠 Homepage",
      keys: [
        "hero_overline", "hero_title", "hero_title_emphasis", "hero_subtitle",
        "hero_cta_primary", "hero_cta_secondary", "hero_scroll_hint",
        "features_overline", "features_title", "features_title_emphasis",
        "features_1_step", "features_1_title", "features_1_body",
        "features_2_step", "features_2_title", "features_2_body",
        "features_3_step", "features_3_title", "features_3_body",
        "features_4_step", "features_4_title", "features_4_body",
        "features_5_step", "features_5_title", "features_5_body",
        "plans_overline", "plans_title",
        "plan_free_name", "plan_free_price", "plan_free_subtitle",
        "plan_free_description", "plan_free_includes", "plan_free_cta",
        "plan_premium_name", "plan_premium_subtitle", "plan_premium_description",
        "plan_premium_includes", "plan_premium_cta",
        "plan_session_name", "plan_session_subtitle", "plan_session_description",
        "plan_session_includes", "plan_session_cta",
        // Testimonials section (auto-hides when no published items)
        "testimonials_overline", "testimonials_title", "testimonials_subtitle", "testimonials_empty",
        "footer_tagline", "footer_privacy", "footer_terms", "footer_rights",
      ],
    },
    {
      id: "carta", label: "✦ Carta gratis",
      keys: [
        "carta_overline", "carta_title", "carta_subtitle",
        "carta_gate_title", "carta_gate_body",
        "carta_label_name", "carta_label_email", "carta_label_date",
        "carta_label_time", "carta_label_place",
        "carta_submit", "carta_loading", "carta_result_title",
        "carta_unlock_title", "carta_unlock_body", "carta_unlock_cta",
      ],
    },
    {
      id: "solicitar", label: "📜 Solicitar mi lectura (textos + precios)",
      keys: [
        "solicitar_overline", "solicitar_title", "solicitar_subtitle",
        "solicitar_plan_label",
        // Step headings
        "solicitar_step1_label", "solicitar_step2_label", "solicitar_step3_label",
        // Reading types
        "reading_type_label",
        "reading_type_triada", "reading_type_triada_subtitle",
        "reading_type_personal", "reading_type_personal_subtitle",
        "reading_type_completo", "reading_type_completo_subtitle",
        "reading_type_revolucion", "reading_type_revolucion_subtitle",
        // Package labels (Step 2)
        "solicitar_plan_premium_label", "solicitar_plan_session_label", "solicitar_plan_session_2_label",
        // Birth data fields (Step 3)
        "solicitar_field_name", "solicitar_field_email",
        "solicitar_field_date", "solicitar_field_time",
        "solicitar_field_place", "solicitar_field_place_placeholder",
        "solicitar_field_notes", "solicitar_field_notes_placeholder",
        // Total + submit + notes
        "solicitar_total_label", "solicitar_total_currency_note", "solicitar_total_empty",
        "solicitar_loading_label", "solicitar_submit",
        "solicitar_payment_note", "solicitar_whatsapp_note",
        "whatsapp_number", "whatsapp_message_template", "admin_notification_email",
        "solicitar_session_note", "solicitar_session_extra_note",
        // Informational legacy fields (price text on homepage cards — unused but kept for back-compat)
        "plan_premium_price", "plan_session_price",
      ],
      extra: "pricing",
    },
    {
      id: "contacto", label: "📩 Contacto",
      keys: [
        "contacto_overline", "contacto_title", "contacto_subtitle",
        "contacto_label_name", "contacto_label_email", "contacto_label_message",
        "contacto_submit", "contacto_success",
      ],
    },
    {
      id: "testimonios", label: "✦ Testimonios (formulario)",
      keys: [
        "testimonial_form_overline", "testimonial_form_title", "testimonial_form_subtitle",
        "testimonial_form_label_name", "testimonial_form_name_placeholder",
        "testimonial_form_label_message", "testimonial_form_message_placeholder",
        "testimonial_form_label_rating", "testimonial_form_submit",
        "testimonial_form_success", "testimonial_form_note",
      ],
    },
    {
      id: "emails", label: "📧 Emails automáticos",
      keys: [
        "birthday_subject", "birthday_html",
        "order_received_subject", "order_received_html",
        "exito_overline", "exito_processing", "exito_paid", "exito_redirect",
        "lectura_overline",
      ],
    },
  ];

  // "Otros" — anything not in a defined section above
  SECTIONS.push({ id: "faq", label: "✦ Preguntas Frecuentes", keys: [], extra: "faq" });
  const known = new Set(SECTIONS.flatMap(s => s.keys));
  const others = Object.keys(defaults).filter(k => !known.has(k) && !k.startsWith("plat_"));
  if (others.length) SECTIONS.push({ id: "otros", label: "🔧 Otros", keys: others });

  const [openSection, setOpenSection] = useState("homepage");

  return (
    <div className="space-y-6">
      <ContentSyncBar onDone={refreshAll} />
      <p className="text-zinc-400 text-sm">
        Edita cualquier texto del sitio. <strong className="text-white">Guardar</strong> aplica el cambio,{" "}
        <strong className="text-white">↺</strong> restablece al valor original.
      </p>
      {SECTIONS.map(section => {
        const isOpen = openSection === section.id;
        return (
          <div key={section.id} className="card">
            <button
              type="button"
              data-testid={`content-section-${section.id}`}
              onClick={() => setOpenSection(isOpen ? null : section.id)}
              className="w-full flex items-center justify-between text-left"
            >
              <p className="font-display text-2xl text-white">{section.label}</p>
              <span className="text-zinc-400">{isOpen ? "−" : "+"}</span>
            </button>
            {isOpen && (
              <div className="mt-6 space-y-4">
                {section.extra === "pricing" && (
                  <PricingEditor onChange={refreshAll} />
                )}
                {section.extra === "faq" && <FaqEditor />}
                {section.keys
                  .filter(k => k in defaults)
                  .map((k) => (
                    <ContentField key={k} k={k} label={KEY_LABELS[k] || k}
                      defaultVal={defaults[k]} currentVal={values[k] ?? defaults[k]}
                      onSave={onSave} onReset={onReset} />
                  ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ContentSyncBar({ onDone }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [autoCfg, setAutoCfg] = useState(null);
  const [autoSaving, setAutoSaving] = useState(false);

  const loadAuto = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/autosync");
      setAutoCfg(data);
      if (!url && data.url) setUrl(data.url);
    } catch { /* */ }
  }, [url]);
  useEffect(() => { loadAuto(); /* eslint-disable-next-line */ }, []);

  const pullFromUrl = async () => {
    if (!url.trim()) { toast.error("Pon la URL del sitio publicado"); return; }
    if (!window.confirm(`¿Sincronizar TODO el contenido y precios desde ${url}? Esto reemplaza los textos editados localmente.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/content/sync-from-url", { url: url.trim() });
      toast.success(`Sincronizado ✦ ${data.imported_content} textos, ${data.imported_pricing} precios`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo sincronizar");
    } finally { setBusy(false); }
  };

  const saveAutoSync = async (patch) => {
    setAutoSaving(true);
    try {
      const payload = {
        url: (patch.url ?? autoCfg?.url ?? url ?? "").trim(),
        interval_minutes: Number(patch.interval_minutes ?? autoCfg?.interval_minutes ?? 5),
        enabled: patch.enabled ?? autoCfg?.enabled ?? false,
      };
      const { data } = await api.put("/admin/autosync", payload);
      setAutoCfg(data);
      toast.success(data.enabled ? `Auto-sync activo cada ${data.interval_minutes} min ✦` : "Auto-sync pausado");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
    finally { setAutoSaving(false); }
  };
  const runAutoSyncNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/autosync/run");
      if (data.error) toast.error(`Auto-sync: ${data.error}`);
      else toast.success(`Auto-sync corrido ✦ ${data.imported_content || 0} textos`);
      loadAuto();
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const exportJson = async () => {
    try {
      const { data } = await api.get("/admin/content/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `eterea-content-${new Date().toISOString().slice(0,10)}.json`;
      a.click();
      toast.success("Descargado ✦");
    } catch { toast.error("No se pudo exportar"); }
  };
  const importJson = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!window.confirm("Importar este JSON reemplaza tus textos actuales con los del archivo. ¿Continuar?")) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const obj = JSON.parse(reader.result);
        const items = obj?.content || {};
        await api.post("/admin/content/bulk-import", { items, replace_all: false });
        if (Array.isArray(obj?.pricing)) {
          for (const row of obj.pricing) {
            await api.put("/admin/pricing", row);
          }
        }
        toast.success("Importación completa ✦");
        onDone?.();
      } catch {
        toast.error("JSON inválido");
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const lastSyncTxt = autoCfg?.last_sync_at
    ? new Date(autoCfg.last_sync_at).toLocaleString("es")
    : "—";
  const lastOk = autoCfg?.last_sync_result?.ok;

  return (
    <div className="card border-[#C9A25E]/30 space-y-6">
      {/* AUTO-SYNC — set-and-forget */}
      <div>
        <div className="flex items-center justify-between gap-3 mb-2">
          <p className="overline">✨ Auto-sync desde producción</p>
          <label className="flex items-center gap-2 cursor-pointer">
            <span className="text-xs text-zinc-400">{autoCfg?.enabled ? "Activo" : "Pausado"}</span>
            <input
              type="checkbox"
              data-testid="autosync-toggle"
              checked={!!autoCfg?.enabled}
              onChange={(e) => saveAutoSync({ enabled: e.target.checked, url: url.trim() })}
              disabled={autoSaving || !url.trim()}
              className="accent-[#C9A25E] w-5 h-5"
            />
          </label>
        </div>
        <p className="text-zinc-400 text-sm mb-3">
          Cuando está activo, este preview <strong className="text-white">jala automáticamente</strong> los textos y precios
          desde tu sitio publicado cada cierto tiempo. <strong className="text-white">Lo que edites en producción aparece aquí solo</strong>.
        </p>
        <div className="flex flex-wrap gap-3 items-stretch">
          <input
            data-testid="sync-url-input"
            className="input-field flex-1 min-w-[260px]"
            placeholder="https://eterea-enlace.com"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <select
            data-testid="autosync-interval"
            className="input-field !py-2"
            value={autoCfg?.interval_minutes ?? 5}
            onChange={(e) => saveAutoSync({ interval_minutes: parseInt(e.target.value), url: url.trim() })}
          >
            <option value={1}>Cada 1 min</option>
            <option value={5}>Cada 5 min</option>
            <option value={15}>Cada 15 min</option>
            <option value={60}>Cada 1 h</option>
            <option value={360}>Cada 6 h</option>
            <option value={1440}>Cada 24 h</option>
          </select>
          <button onClick={runAutoSyncNow} disabled={busy || !url.trim()}
            data-testid="autosync-run-now" className="btn-lilac !py-2 text-sm">
            {busy ? "Ejecutando…" : "Ejecutar ahora"}
          </button>
        </div>
        <p className="text-xs text-zinc-500 mt-3">
          Última corrida: <span className={lastOk === false ? "text-red-400" : "text-zinc-300"}>{lastSyncTxt}</span>
          {autoCfg?.last_sync_result?.ok && (
            <> · {autoCfg.last_sync_result.imported_content} textos, {autoCfg.last_sync_result.imported_pricing} precios</>
          )}
          {autoCfg?.last_sync_result?.error && (
            <> · <span className="text-red-400">{autoCfg.last_sync_result.error}</span></>
          )}
        </p>
      </div>

      <div className="border-t border-white/5 pt-4">
        <p className="overline mb-2">🔄 Sincronización manual</p>
        <p className="text-zinc-500 text-xs mb-3">¿Querés pullear una sola vez sin activar el auto-sync? Usa esto.</p>
        <div className="flex flex-wrap gap-3 items-stretch">
          <button onClick={pullFromUrl} disabled={busy} data-testid="sync-pull-btn"
            className="btn-ghost !py-2 text-sm">
            {busy ? "Sincronizando…" : "🔄 Sincronizar desde URL ahora"}
          </button>
          <button onClick={exportJson} data-testid="sync-export-btn" className="btn-ghost !py-2 text-sm">
            ⬇ Exportar JSON
          </button>
          <label className="btn-ghost !py-2 text-sm cursor-pointer">
            ⬆ Importar JSON
            <input type="file" accept="application/json" onChange={importJson}
              data-testid="sync-import-input" className="hidden" />
          </label>
        </div>
      </div>
    </div>
  );
}

function PricingEditor({ onChange }) {
  const [rows, setRows] = useState([]);
  const [defaults, setDefaults] = useState({});
  const [readings, setReadings] = useState([]);
  const load = useCallback(async () => {
    try {
      const [{ data: p }, { data: r }] = await Promise.all([
        api.get("/admin/pricing"),
        api.get("/admin/readings"),
      ]);
      setRows(p.items); setDefaults(p.defaults); setReadings(r.items);
    } catch { /* */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleReading = async (readingType, enabled) => {
    try {
      await api.put("/admin/readings", { reading_type: readingType, enabled });
      setReadings(prev => prev.map(r => r.reading_type === readingType ? { ...r, enabled } : r));
      toast.success(enabled ? `"${readingType}" habilitada ✦` : `"${readingType}" deshabilitada`);
    } catch { toast.error("No se pudo cambiar"); }
  };

  const updateRow = (idx, field, value) => {
    const next = [...rows];
    next[idx] = { ...next[idx], [field]: value };
    setRows(next);
  };
  const saveRow = async (idx) => {
    const r = rows[idx];
    try {
      await api.put("/admin/pricing", {
        key: r.key,
        usd: parseFloat(r.usd) || 0,
        uyu: parseFloat(r.uyu) || 0,
        session_count: parseInt(r.session_count) || 0,
      });
      toast.success("Precio guardado ✦");
      onChange?.();
    } catch { toast.error("No se pudo guardar"); }
  };
  const resetRow = async (idx) => {
    const r = rows[idx];
    try {
      await api.delete(`/admin/pricing/${encodeURIComponent(r.key)}`);
      toast.success("Restaurado");
      load();
      onChange?.();
    } catch { toast.error("Error"); }
  };

  const READING_LABELS = {
    "Tríada": "El Faro Interno",
    "Personal": "Las Herramientas del Navegante",
    "Completo": "La Travesía Completa",
    "Revolución Solar": "Revolución Solar",
  };
  const PLAN_LABELS = { premium: "Solo Reporte", session: "Reporte + Sesión 1:1" };

  return (
    <div className="border border-[#C9A25E]/20 bg-[#C9A25E]/5 p-5 mb-6">
      <p className="overline mb-3">💰 Precios y disponibilidad (sólo se muestran en Solicitar)</p>

      {/* Availability toggles */}
      <div className="mb-5">
        <p className="text-xs text-zinc-500 mb-3 tracking-wide uppercase">Disponibilidad de lecturas</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {readings.map((r) => (
            <button key={r.reading_type}
              data-testid={`reading-toggle-${r.reading_type.toLowerCase().replace(/\s/g,'-').normalize('NFD').replace(/[\u0300-\u036f]/g,'')}`}
              onClick={() => toggleReading(r.reading_type, !r.enabled)}
              className={`flex items-center justify-between gap-3 p-3 border transition text-left ${
                r.enabled
                  ? "border-emerald-400/30 bg-emerald-400/5 hover:border-emerald-400/50"
                  : "border-white/10 bg-white/[0.02] hover:border-white/25"
              }`}>
              <span className={`text-sm ${r.enabled ? "text-white" : "text-zinc-500"}`}>
                {READING_LABELS[r.reading_type] || r.reading_type}
              </span>
              <span className={`text-[10px] tracking-[0.15em] uppercase whitespace-nowrap ${
                r.enabled ? "text-emerald-400" : "text-zinc-500"
              }`}>
                {r.enabled ? "✦ Habilitada" : "No disponible"}
              </span>
            </button>
          ))}
        </div>
        <p className="text-xs text-zinc-600 mt-2">
          Las lecturas deshabilitadas aparecen en Solicitar pero no pueden seleccionarse — muestran "No disponible".
        </p>
      </div>

      <p className="text-zinc-400 text-xs mb-4">
        UYU es informativo. El cobro real se procesa en USD vía Stripe.
        Cuando integremos Mercado Pago, el monto UYU se cobrará localmente.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-zinc-500 text-xs">
              <th className="text-left p-2">Lectura</th>
              <th className="text-left p-2">Paquete</th>
              <th className="text-right p-2">UYU</th>
              <th className="text-right p-2">USD</th>
              <th className="text-right p-2">Sesiones</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const d = defaults[r.key] || {};
              const dirty = (parseFloat(r.usd) !== d.usd) || (parseFloat(r.uyu) !== d.uyu) || (parseInt(r.session_count) !== d.session_count);
              return (
                <tr key={r.key} data-testid={`pricing-row-${r.key}`} className="border-t border-white/5">
                  <td className="p-2 text-white">{READING_LABELS[r.reading_type] || r.reading_type}</td>
                  <td className="p-2 text-zinc-300">{PLAN_LABELS[r.plan] || r.plan}</td>
                  <td className="p-2"><input data-testid={`pricing-uyu-${r.key}`}
                    className="input-field !py-1 !px-2 text-right w-24 ml-auto" type="number" value={r.uyu}
                    onChange={(e) => updateRow(i, "uyu", e.target.value)} /></td>
                  <td className="p-2"><input data-testid={`pricing-usd-${r.key}`}
                    className="input-field !py-1 !px-2 text-right w-20 ml-auto" type="number" step="0.01" value={r.usd}
                    onChange={(e) => updateRow(i, "usd", e.target.value)} /></td>
                  <td className="p-2"><input data-testid={`pricing-sc-${r.key}`}
                    className="input-field !py-1 !px-2 text-right w-14 ml-auto" type="number" value={r.session_count}
                    onChange={(e) => updateRow(i, "session_count", e.target.value)}
                    disabled={r.plan !== "session"} /></td>
                  <td className="p-2 text-right whitespace-nowrap">
                    <button onClick={() => saveRow(i)} data-testid={`pricing-save-${r.key}`}
                      className={`!py-1 !px-2 text-xs mr-1 ${dirty ? "btn-lilac" : "btn-ghost opacity-50"}`}>Guardar</button>
                    <button onClick={() => resetRow(i)} className="btn-ghost !py-1 !px-2 text-xs">↺</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BirthdaysCalendar({ source }) {
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());
  const [data, setData] = useState({ by_day: {}, days_in_month: 30, total: 0 });
  const [selected, setSelected] = useState(null);  // day number

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/birthdays", { params: { source, month, year } });
      setData(data);
    } catch { /* */ }
  }, [source, month, year]);
  useEffect(() => { load(); }, [load]);

  const MONTHS = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
  // First weekday of month (0=Sun → shift to Mon-start: 6)
  const firstWeekday = new Date(year, month - 1, 1).getDay();
  const offsetDays = (firstWeekday + 6) % 7;  // Monday-start grid
  const cells = [];
  for (let i = 0; i < offsetDays; i++) cells.push(null);
  for (let d = 1; d <= data.days_in_month; d++) cells.push(d);

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(year - 1); } else setMonth(month - 1);
    setSelected(null);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(year + 1); } else setMonth(month + 1);
    setSelected(null);
  };
  const isToday = (d) => today.getDate() === d && today.getMonth() + 1 === month && today.getFullYear() === year;
  const peopleForDay = (d) => data.by_day?.[d] || [];

  return (
    <div data-testid={`birthdays-${source}`}>
      <div className="flex items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <button onClick={prevMonth} data-testid="birthdays-prev"
            className="btn-ghost !py-1 !px-3 text-sm">←</button>
          <p className="font-display text-2xl text-white min-w-[200px] text-center">
            {MONTHS[month - 1]} {year}
          </p>
          <button onClick={nextMonth} data-testid="birthdays-next"
            className="btn-ghost !py-1 !px-3 text-sm">→</button>
        </div>
        <p className="text-sm text-zinc-500"><strong className="text-[#C9A25E]">{data.total}</strong> cumpleaños este mes</p>
      </div>

      <div className="grid grid-cols-7 gap-1 text-xs text-zinc-500 mb-2 px-1">
        {["L","M","X","J","V","S","D"].map(d => <div key={d} className="text-center py-1 tracking-widest">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (d === null) return <div key={`e${i}`} />;
          const people = peopleForDay(d);
          const has = people.length > 0;
          const isSel = selected === d;
          return (
            <button
              key={d}
              data-testid={`birthday-day-${d}`}
              onClick={() => setSelected(isSel ? null : d)}
              className={`aspect-square p-1.5 border text-left flex flex-col justify-between transition relative
                ${isSel ? "border-[#C9A25E] bg-[#C9A25E]/20"
                       : has ? "border-[#C9A25E]/50 bg-[#C9A25E]/5 hover:bg-[#C9A25E]/15"
                             : "border-white/5 hover:border-white/15"}
                ${isToday(d) ? "ring-1 ring-[#C9A25E]/40" : ""}
              `}
            >
              <span className={`text-xs ${has ? "text-white font-medium" : "text-zinc-500"}`}>{d}</span>
              {has && (
                <span className="text-[10px] text-[#C9A25E] font-mono self-end">
                  {people.length} ✦
                </span>
              )}
            </button>
          );
        })}
      </div>

      {selected && (
        <div className="mt-6 card border-[#C9A25E]/30">
          <p className="overline mb-3">{selected} de {MONTHS[month - 1]} · {peopleForDay(selected).length} persona(s)</p>
          {peopleForDay(selected).length === 0 ? (
            <p className="text-zinc-500 italic text-sm">Sin cumpleaños este día.</p>
          ) : (
            <div className="space-y-3">
              {peopleForDay(selected).map((p, idx) => (
                <div key={p.id || idx} className="p-4 border border-white/10 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-display text-lg text-white">{p.name}</p>
                    <p className="text-xs text-zinc-500">{p.email}</p>
                    {p.birth_place && <p className="text-xs text-zinc-600 mt-1">Nacida {p.birth_date} · {p.birth_place}</p>}
                    {source === "orders" && (
                      <p className="text-xs text-[#C9A25E] mt-1">
                        {p.plan === "session" ? "Reporte + Sesión 1:1" : "Solo Reporte"} · {p.reading_type}
                        {p.paid ? "" : " · sin pagar"}
                      </p>
                    )}
                  </div>
                  <a href={`mailto:${p.email}?subject=${encodeURIComponent("Feliz cumpleaños ✦")}`}
                    data-testid={`birthday-mail-${idx}`}
                    className="btn-lilac !py-2 !px-3 text-xs">✉ Enviar email</a>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
